import React from "react";
import { Activity, ChevronRight } from "lucide-react";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { useStackMode } from "@/lib/stackModeContext";
import { resolve } from "@/lib/rce/resolver";
import { LABELS } from "@/lib/displayLabels";

const val = (r) => (r && r.confidence !== "unknown" && r.value !== "" && r.value != null ? r.value : null);

// Current Operations — compact live operational-context card. Single Dashboard
// gateway to Rig Operations. Read-only: resolves existing values only, never
// fabricates data, never gates calculator access.
export const CurrentOperationsCard = () => {
  const { session } = useEngineeringSession();
  const { stackMode } = useStackMode();

  // An ACTIVE operation means an operation was actually chosen — not merely
  // that a rig or well is linked. Both the Rig Sheet and Well Profile editors
  // auto-link their record to the session on first save, so keying off rigId
  // /wellId reported ACTIVE for a context the operator never established.
  const hasContext = Boolean(session && String(session.currentOperation || "").trim());

  const header = (
    <div className="flex items-center justify-between gap-2">
      <div className="label-up text-[var(--rs-text-3)] inline-flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" style={{ color: hasContext ? "#4ADE80" : "var(--rs-text-3)" }} /> Current Operations
      </div>
      {hasContext && (
        <span
          data-testid="current-operations-active-badge"
          className="label-up text-[8.5px] px-1.5 py-0.5 rounded shrink-0"
          style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.35)" }}
        >
          Active
        </span>
      )}
    </div>
  );

  // P0 fix (Phase 3) — this used to open the legacy rsc.profiles.opstate.v1
  // editor, a duplicate data-entry surface for data the RCE session /
  // canonical editors already capture. Now opens the same canonical
  // Session Switcher used elsewhere in the app (Home stage, ProfileEditor's
  // own "Next → Rig Engineering") instead of a legacy form.
  const openOpsAction = (
    <button
      type="button"
      data-testid="current-operations-open-opstates"
      onClick={() => window.dispatchEvent(new CustomEvent("rce:open-session-switcher"))}
      className="font-mono-rs text-[10px] tracking-[0.18em] uppercase mt-2 inline-flex items-center gap-1 font-semibold"
      style={{ color: "var(--rs-amber)" }}
    >
      Open {LABELS.engSession} <ChevronRight className="w-3 h-3" />
    </button>
  );

  if (!hasContext) {
    return (
      <section data-testid="current-operations-empty" className="card-rs px-3.5 py-3">
        {header}
        <div className="text-[13px] font-semibold mt-1.5">No current operation</div>
        <p className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
          Connect a rig, well or operation via {LABELS.engSession} to establish the current operating context.
        </p>
        {openOpsAction}
      </section>
    );
  }

  const ctx = { rigId: session.rigId, wellId: session.wellId };
  const md = val(resolve("well.currentMdFt", ctx));
  const rows = [
    ["Rig", val(resolve("rig.name", ctx))],
    ["Well", val(resolve("well.name", ctx))],
    ["Operation", session.currentOperation || null],
    ["Hole Section", session.holeSection || val(resolve("well.holeSection", ctx))],
    ["Depth (MD)", md ? `${md} ft` : null],
    ["Stack", stackMode === "subsea" ? "Subsea" : "Surface"],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <section data-testid="current-operations-card" className="card-rs px-3.5 py-3">
      {header}
      {rows.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
          {rows.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <div className="text-[9.5px] label-up text-[var(--rs-text-3)]">{label}</div>
              <div className="text-[12.5px] font-semibold truncate" data-testid={`curop-${label.toLowerCase().replace(/[\s()]+/g, "-").replace(/-$/, "")}`}>{String(value)}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-[var(--rs-text-3)] mt-1.5">Context selected — no resolvable operational values yet.</p>
      )}
      {openOpsAction}
    </section>
  );
};
